import { calculate } from '@calculations/price/strategies/fixed-market/calculate';
import { SnapRoundingOptionsCustom } from '@calculations/round';

import {
  OnQuantityChangeInput,
  OnQuantityChangeOutput,
  OnQuotePriceCreationInput,
  OnQuotePriceCreationOutput,
  RecalculatePriceStrategy,
} from '../types';
import { getAllowFallback } from '../utilities/get-allow-fallback';
import { noOpUnitPrice } from './no-op';

function onQuantityChange(
  inputs: OnQuantityChangeInput,
): OnQuantityChangeOutput {
  const { state, quoteLine, quotePrice, quantity, priceStrategyConfigs } =
    inputs;

  if (!state.marketCode) {
    return {
      status: 'failure',
      reason: 'No market code found',
    };
  }

  if (!quoteLine.marketPrices) {
    return {
      status: 'failure',
      reason: 'No market prices found',
    };
  }

  const allowFallback = getAllowFallback(
    quotePrice.priceStrategy,
    priceStrategyConfigs,
  );

  const output = calculate(
    {
      quantity,
      allowFallback,
      marketPrices: quoteLine.marketPrices,
      marketCode: state.marketCode,
    },
    SnapRoundingOptionsCustom(state),
  );

  if (!output) {
    return {
      status: 'failure',
      reason: 'Price calculation failed',
    };
  }

  return {
    status: 'success',
    unitPrice: output.unitPrice,
  };
}

function onQuotePriceCreation(
  inputs: OnQuotePriceCreationInput,
): OnQuotePriceCreationOutput {
  const { state, quoteLine, quotePrice, priceStrategyConfigs } = inputs;

  if (!state.marketCode) {
    return {
      status: 'failure',
      reason: 'No market code found',
    };
  }

  if (!quoteLine.marketPrices) {
    return {
      status: 'failure',
      reason: 'No market prices found',
    };
  }

  const allowFallback = getAllowFallback(
    quotePrice.priceStrategy,
    priceStrategyConfigs,
  );

  const output = calculate(
    {
      quantity: quotePrice.quantity,
      allowFallback,
      marketPrices: quoteLine.marketPrices,
      marketCode: state.marketCode,
    },
    SnapRoundingOptionsCustom(state),
  );

  if (!output) {
    return {
      status: 'failure',
      reason: 'Price calculation failed',
    };
  }

  return {
    status: 'success',
    quotePrice: {
      ...quotePrice,
      unitPrice: output.unitPrice,
    },
  };
}

export const recalculateFixedMarket = {
  onQuantityChange,
  onUnitPriceChange: noOpUnitPrice,
  onGrossMarginChange: null,
  onContributionMarginChange: null,
  onBaseGrossMarginChange: null,
  onQuotePriceCreation,
  onUnitCostChange: null,
} satisfies RecalculatePriceStrategy;
